import { motion } from 'framer-motion'

interface PivotItem {
  identifier: string
  type: 'phone' | 'email' | 'username'
  caller_id_name: string | null
  gmail_data: {
    gaia_id?: string
    profile_picture?: string
    activated_services?: string[]
    last_profile_edit?: string
  } | null
  new_emails: string[]
  new_phones: string[]
  new_names: string[]
  breach_sources: string[]
  node_count: number
  error: string | null
}

interface PivotEnrichResult {
  seed: string
  pivots: PivotItem[]
  aggregate: {
    total_new_nodes: number
    all_new_emails: string[]
    all_new_phones: string[]
    all_new_names: string[]
  }
}

interface Props {
  query: string
  phones: string[]
  emails: string[]
  usernames: string[]
  result: PivotEnrichResult | null
  loading: boolean
  error: string | null
  onRun: () => void
}

const TYPE_ICON: Record<string, string> = {
  phone: '📞',
  email: '📧',
  username: '👤',
}

const TYPE_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  phone:    { text: 'text-green-300',  bg: 'bg-green-950/30',  border: 'border-green-800/40' },
  email:    { text: 'text-blue-300',   bg: 'bg-blue-950/30',   border: 'border-blue-800/40' },
  username: { text: 'text-purple-300', bg: 'bg-purple-950/30', border: 'border-purple-800/40' },
}

const PIPELINE_STAGES = [
  'Breach database search on discovered phones',
  'Breach database search on discovered emails',
  'Caller ID lookup on phones',
  'Gmail OSINT on @gmail.com addresses',
  'Aggregating new identifiers',
]

export function BreachPivotEnrichment({ phones, emails, usernames, result, loading, error, onRun }: Props) {
  const totalTargets = phones.length + emails.length + usernames.length

  // ── Trigger state ──────────────────────────────────────────────────────────
  if (!result && !loading && !error) {
    return (
      <div className="border border-indigo-900/40 rounded-xl bg-indigo-950/10 overflow-hidden">
        <div className="px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] text-indigo-400 tracking-widest mb-1 uppercase">Breach Pivot Enrichment</div>
              <div className="font-mono text-sm text-white font-bold mb-2">
                Pivot on {totalTargets} discovered identifier{totalTargets !== 1 ? 's' : ''}
              </div>
              <div className="font-mono text-[11px] text-gray-500 leading-relaxed mb-3">
                Runs a fresh breach search on each phone, email, and username found in the breach records —
                pivoting through the same database to uncover additional linked identifiers.
                Also runs Caller ID on phones and Gmail OSINT on Gmail addresses.
              </div>
              {/* Show what will be pivoted */}
              <div className="space-y-1.5">
                {phones.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[9px] text-gray-600 w-16 shrink-0">Phones:</span>
                    {phones.map(p => (
                      <span key={p} className="font-mono text-[10px] text-green-400 bg-green-950/30 border border-green-800/40 px-2 py-0.5 rounded">{p}</span>
                    ))}
                  </div>
                )}
                {emails.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[9px] text-gray-600 w-16 shrink-0">Emails:</span>
                    {emails.map(e => (
                      <span key={e} className="font-mono text-[10px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded">{e}</span>
                    ))}
                  </div>
                )}
                {usernames.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[9px] text-gray-600 w-16 shrink-0">Usernames:</span>
                    {usernames.map(u => (
                      <span key={u} className="font-mono text-[10px] text-purple-400 bg-purple-950/30 border border-purple-800/40 px-2 py-0.5 rounded">{u}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onRun}
              disabled={totalTargets === 0}
              className="shrink-0 px-4 py-2.5 font-mono text-xs text-indigo-300 border border-indigo-500/40 rounded-lg bg-indigo-950/30 hover:bg-indigo-950/60 hover:border-indigo-400/60 transition-all font-bold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Run Pivot
            </button>
          </div>
          {totalTargets === 0 && (
            <div className="mt-3 font-mono text-[10px] text-gray-600">
              No pivot targets found in breach records — search must return phone numbers, alternate emails, or usernames.
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="border border-indigo-900/40 rounded-xl bg-indigo-950/10 p-5">
        <div className="flex items-center gap-2 mb-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full shrink-0"
          />
          <div className="font-mono text-[9px] text-indigo-400 tracking-widest">
            BREACH PIVOT ENRICHMENT — RUNNING {totalTargets} PIVOT{totalTargets !== 1 ? 'S' : ''}
          </div>
        </div>
        <div className="space-y-2">
          {PIPELINE_STAGES.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0.2 }}
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ duration: 2.5, delay: i * 0.5, repeat: Infinity }}
              className="flex items-center gap-2"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/50 shrink-0" />
              <span className="font-mono text-[10px] text-gray-500">{s}</span>
            </motion.div>
          ))}
        </div>
        <div className="mt-4 text-center font-mono text-[10px] text-gray-700">
          Searching {totalTargets} identifier{totalTargets !== 1 ? 's' : ''} in parallel — typically 10–30 seconds
        </div>
      </div>
    )
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="border border-red-900/40 rounded-xl bg-red-950/10 p-4">
        <div className="font-mono text-[9px] text-red-400 tracking-widest mb-1">PIVOT ENRICHMENT FAILED</div>
        <div className="font-mono text-xs text-gray-400 mb-3">{error}</div>
        <button onClick={onRun} className="px-3 py-1.5 font-mono text-[10px] text-red-300 border border-red-800/40 rounded bg-red-950/20 hover:bg-red-950/40 transition-colors">
          Retry
        </button>
      </div>
    )
  }

  // ── Results ────────────────────────────────────────────────────────────────
  const { pivots = [], aggregate } = result!
  const successPivots = pivots.filter(p => !p.error || p.node_count > 0)
  const totalNewIds = (aggregate.all_new_emails.length + aggregate.all_new_phones.length + aggregate.all_new_names.length)
  const copyVal = (v: string) => { try { navigator.clipboard?.writeText(v) } catch (_) {} }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      {/* Summary header */}
      <div className="border border-indigo-900/40 rounded-xl bg-indigo-950/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[9px] text-indigo-400 tracking-widest">BREACH PIVOT ENRICHMENT — COMPLETE</div>
          <div className="font-mono text-xs text-gray-300 mt-0.5">
            {pivots.length} pivot{pivots.length !== 1 ? 's' : ''} run · {aggregate.total_new_nodes} nodes found ·{' '}
            <span className={totalNewIds > 0 ? 'text-green-400 font-bold' : 'text-gray-500'}>
              {totalNewIds} new identifier{totalNewIds !== 1 ? 's' : ''} discovered
            </span>
          </div>
        </div>
        <button
          onClick={onRun}
          className="shrink-0 font-mono text-[9px] text-gray-600 border border-gray-800/40 px-2.5 py-1 rounded hover:text-gray-400 hover:border-gray-700/40 transition-colors"
        >
          Re-run
        </button>
      </div>

      {/* Aggregate new identifiers (if any found) */}
      {totalNewIds > 0 && (
        <div className="border border-green-900/40 rounded-xl bg-green-950/10 p-4">
          <div className="font-mono text-[9px] text-green-400 tracking-widest mb-2.5 font-bold">
            NEW IDENTIFIERS DISCOVERED THROUGH PIVOTS
          </div>
          <div className="space-y-2">
            {aggregate.all_new_emails.length > 0 && (
              <div>
                <div className="font-mono text-[9px] text-gray-600 mb-1">EMAILS</div>
                <div className="flex flex-wrap gap-1.5">
                  {aggregate.all_new_emails.map(e => (
                    <button key={e} onClick={() => copyVal(e)}
                      className="font-mono text-[10px] text-blue-300 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded hover:bg-blue-950/60 transition-colors"
                      title="Click to copy">
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {aggregate.all_new_phones.length > 0 && (
              <div>
                <div className="font-mono text-[9px] text-gray-600 mb-1">PHONES</div>
                <div className="flex flex-wrap gap-1.5">
                  {aggregate.all_new_phones.map(p => (
                    <button key={p} onClick={() => copyVal(p)}
                      className="font-mono text-[10px] text-green-300 bg-green-950/30 border border-green-800/40 px-2 py-0.5 rounded hover:bg-green-950/60 transition-colors"
                      title="Click to copy">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {aggregate.all_new_names.length > 0 && (
              <div>
                <div className="font-mono text-[9px] text-gray-600 mb-1">NAMES</div>
                <div className="flex flex-wrap gap-1.5">
                  {aggregate.all_new_names.map(n => (
                    <button key={n} onClick={() => copyVal(n)}
                      className="font-mono text-[10px] text-yellow-300 bg-yellow-950/30 border border-yellow-800/40 px-2 py-0.5 rounded hover:bg-yellow-950/60 transition-colors"
                      title="Click to copy">
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-pivot results */}
      {successPivots.length > 0 && (
        <div className="border border-white/[0.06] rounded-xl bg-white/[0.02] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/[0.05]">
            <span className="font-mono text-[9px] text-gray-500 tracking-widest uppercase">
              Per-Pivot Findings
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {pivots.map((pv, i) => {
              const c = TYPE_COLORS[pv.type] || TYPE_COLORS.email
              const hasFindings = pv.node_count > 0 || pv.caller_id_name || pv.gmail_data
              const newIds = pv.new_emails.length + pv.new_phones.length + pv.new_names.length
              return (
                <div key={i} className={`px-4 py-3 ${pv.error && !hasFindings ? 'opacity-40' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 font-mono text-[9px] px-1.5 py-0.5 rounded border ${c.bg} ${c.border} ${c.text} mt-0.5`}>
                      {TYPE_ICON[pv.type]} {pv.type.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-white font-bold">{pv.identifier}</span>
                        {pv.caller_id_name && (
                          <span className="font-mono text-[10px] text-green-300 bg-green-950/30 border border-green-800/40 px-2 py-0.5 rounded">
                            ID: {pv.caller_id_name}
                          </span>
                        )}
                        {pv.gmail_data?.gaia_id && (
                          <span className="font-mono text-[10px] text-blue-300 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded">
                            GAIA:{pv.gmail_data.gaia_id}
                          </span>
                        )}
                      </div>

                      {/* Node count + breach sources */}
                      {pv.node_count > 0 && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="font-mono text-[10px] text-gray-400">
                            {pv.node_count} node{pv.node_count !== 1 ? 's' : ''} found
                          </span>
                          {pv.breach_sources.length > 0 && (
                            <span className="font-mono text-[9px] text-gray-700" title={pv.breach_sources.join(', ')}>
                              via {pv.breach_sources.slice(0, 3).join(', ')}{pv.breach_sources.length > 3 ? ` +${pv.breach_sources.length - 3}` : ''}
                            </span>
                          )}
                          {newIds > 0 && (
                            <span className="font-mono text-[9px] text-green-400 font-bold">
                              +{newIds} new identifier{newIds !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      )}

                      {/* New emails */}
                      {pv.new_emails.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {pv.new_emails.map(e => (
                            <button key={e} onClick={() => copyVal(e)}
                              className="font-mono text-[9px] text-blue-300 bg-blue-950/30 border border-blue-800/40 px-1.5 py-0.5 rounded hover:bg-blue-950/60"
                              title="Click to copy">
                              {e}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* New phones */}
                      {pv.new_phones.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pv.new_phones.map(p => (
                            <button key={p} onClick={() => copyVal(p)}
                              className="font-mono text-[9px] text-green-300 bg-green-950/30 border border-green-800/40 px-1.5 py-0.5 rounded hover:bg-green-950/60"
                              title="Click to copy">
                              {p}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* New names */}
                      {pv.new_names.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pv.new_names.map(n => (
                            <button key={n} onClick={() => copyVal(n)}
                              className="font-mono text-[9px] text-yellow-300 bg-yellow-950/30 border border-yellow-800/40 px-1.5 py-0.5 rounded hover:bg-yellow-950/60"
                              title="Click to copy">
                              {n}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Gmail activated services */}
                      {pv.gmail_data?.activated_services && pv.gmail_data.activated_services.length > 0 && (
                        <div className="mt-1.5">
                          <span className="font-mono text-[9px] text-gray-600">Google services: </span>
                          <span className="font-mono text-[9px] text-gray-400">
                            {pv.gmail_data.activated_services.slice(0, 6).join(' · ')}
                          </span>
                        </div>
                      )}

                      {/* Error */}
                      {pv.error && !hasFindings && (
                        <div className="font-mono text-[9px] text-red-600 mt-1">{pv.error}</div>
                      )}

                      {/* No findings */}
                      {!hasFindings && !pv.error && (
                        <div className="font-mono text-[9px] text-gray-700 mt-1">No additional data found</div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* All pivots returned nothing */}
      {totalNewIds === 0 && successPivots.length === 0 && (
        <div className="border border-gray-800/40 rounded-xl bg-white/[0.02] p-6 text-center">
          <div className="font-mono text-[10px] text-gray-600 tracking-widest mb-1">NO ADDITIONAL DATA FOUND</div>
          <div className="font-mono text-xs text-gray-700">
            Breach searches on the discovered identifiers returned no additional linked records.
          </div>
        </div>
      )}
    </motion.div>
  )
}
